import { useCallback, useMemo, useState } from "react";
import { useAppDispatch, useAppSelector } from "../redux";
import {
  getPartnerTypesSagaAction,
  getPartnerVerticalSagaAction,
  getRevenueStreamSagaAction,
  getPartnerTierSagaAction,
} from "../redux/saga/partner-details/saga-actions";
import {
  selectFormLookupData,
  selectPartnerDetailsLoading,
  selectAddPartnerLoading,
  selectUpdatePartnerLoading,
} from "../redux/slices/partner-details/selectors";
import parsePhoneNumberFromString from "libphonenumber-js";
import { CountryCode } from "../utils/CountryCode";
import { Constants } from "../utils/constants";

export interface PartnerFormData {
  name: string;
  email: string;
  accountManagerEmail: string;
  type: string[];
  potential: string;
  website: string;
  description: string;
  region: string;
  revenueStream: string;
  partnerVertical: string[];
  partnerTier: string;
  country: string;
  state: string;
  countryCode: string;
  phoneNumber: string;
  streetAddress: string;
  registrationDate: Date | null;
}

export interface ValidationState {
  emailStatus: { status: "success" | "error" } | {};
  phoneStatus: { status: "success" | "error" } | {};
  accountManagerEmailStatus: { status: "success" | "error" } | {};
  errorMsg: {
    emailMsg: string;
    phoneMsg: string;
    accountManagerEmailMsg: string;
  };
}

const initialFormData: PartnerFormData = {
  name: "",
  email: "",
  accountManagerEmail: "",
  type: [],
  potential: "Prime",
  website: "",
  description: "",
  region: Constants.REGIONS[0],
  revenueStream: "",
  partnerVertical: [],
  partnerTier: "",
  country: "",
  state: "",
  countryCode: "",
  phoneNumber: "",
  streetAddress: "",
  registrationDate: null,
};

const initialValidationState: ValidationState = {
  emailStatus: {},
  phoneStatus: {},
  accountManagerEmailStatus: {},
  errorMsg: {
    emailMsg: "",
    phoneMsg: "",
    accountManagerEmailMsg: "",
  },
};

export const usePartnerForm = (initialData?: Partial<PartnerFormData>) => {
  const dispatch = useAppDispatch();
  const { partnerTypes, revenueStream, partnerVertical, partnerTier } =
    useAppSelector(selectFormLookupData);
  const isLoading = useAppSelector(selectPartnerDetailsLoading);
  const isAddLoading = useAppSelector(selectAddPartnerLoading);
  const isUpdateLoading = useAppSelector(selectUpdatePartnerLoading);

  const [formData, setFormData] = useState<PartnerFormData>({
    ...initialFormData,
    ...initialData,
  });

  const [validationState, setValidationState] = useState<ValidationState>(
    initialValidationState
  );
  const [selectedCountryCode, setSelectedCountryCode] = useState<{
    label: string;
    value: string;
  } | null>(null);
  const [selectedCountry, setSelectedCountry] = useState<{
    label: string;
    value: string;
  } | null>(null);

  // Memoized constants
  const emailRegex = useMemo(
    () => /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
    []
  );
  const digitRegex = useMemo(() => /^\d+$/, []);

  // Memoized options
  const countryCodeOptions = useMemo(
    () =>
      CountryCode.map((country) => ({
        label: country.name,
        value: country.dial_code,
      })),
    []
  );

  const countryOptions = useMemo(
    () => [
      { label: "Global", value: "Global" },
      ...CountryCode.map((country) => ({
        label: country.name,
        value: country.name,
      })),
    ],
    []
  );

  const regionOptions = useMemo(
    () =>
      Constants.REGIONS.map((region) => ({
        label: region,
        value: region,
      })),
    []
  );

  const revenueStreamOptions = useMemo(
    () =>
      revenueStream.map((item: any) => ({
        label: item.value,
        value: item.value,
      })),
    [revenueStream]
  );

  const partnerVerticalOptions = useMemo(
    () =>
      partnerVertical.map((item: any) => ({
        label: item.value,
        value: item.value,
      })),
    [partnerVertical]
  );

  const partnerTierOptions = useMemo(
    () =>
      partnerTier.map((item: any) => ({
        label: item.value,
        value: item.value,
      })),
    [partnerTier]
  );

  // Initialize lookup data
  const initializeLookupData = useCallback(() => {
    if (partnerTypes.length === 0) {
      dispatch(getPartnerTypesSagaAction());
    }
    if (revenueStream.length === 0) {
      dispatch(getRevenueStreamSagaAction());
    }
    if (partnerVertical.length === 0) {
      dispatch(getPartnerVerticalSagaAction());
    }
    if (partnerTier.length === 0) {
      dispatch(getPartnerTierSagaAction());
    }
  }, [
    dispatch,
    partnerTypes.length,
    revenueStream.length,
    partnerVertical.length,
    partnerTier.length,
  ]);

  // Validation functions
  const validateEmail = useCallback(
    (email: string) => {
      if (email && !emailRegex.test(email)) {
        setValidationState((prev) => ({
          ...prev,
          emailStatus: { status: "error" },
          errorMsg: { ...prev.errorMsg, emailMsg: "Enter valid email-id" },
        }));
        return false;
      }
      setValidationState((prev) => ({
        ...prev,
        emailStatus: {},
        errorMsg: { ...prev.errorMsg, emailMsg: "" },
      }));
      return true;
    },
    [emailRegex]
  );

  const validateAccountManagerEmail = useCallback(
    (email: string) => {
      if (email && !emailRegex.test(email)) {
        setValidationState((prev) => ({
          ...prev,
          accountManagerEmailStatus: { status: "error" },
          errorMsg: {
            ...prev.errorMsg,
            accountManagerEmailMsg: "Enter valid email-id",
          },
        }));
        return false;
      }
      setValidationState((prev) => ({
        ...prev,
        accountManagerEmailStatus: {},
        errorMsg: { ...prev.errorMsg, accountManagerEmailMsg: "" },
      }));
      return true;
    },
    [emailRegex]
  );

  const validatePhone = useCallback(
    (phoneNumber: string, countryCode: string) => {
      if (phoneNumber.length === 0) {
        setValidationState((prev) => ({
          ...prev,
          phoneStatus: {},
          errorMsg: { ...prev.errorMsg, phoneMsg: "" },
        }));
        return true;
      }

      const parsedPhoneNumber = parsePhoneNumberFromString(
        countryCode + phoneNumber
      );
      if (
        parsedPhoneNumber &&
        parsedPhoneNumber.isValid() &&
        digitRegex.test(phoneNumber)
      ) {
        setValidationState((prev) => ({
          ...prev,
          phoneStatus: {},
          errorMsg: { ...prev.errorMsg, phoneMsg: "" },
        }));
        return true;
      }

      setValidationState((prev) => ({
        ...prev,
        phoneStatus: { status: "error" },
        errorMsg: { ...prev.errorMsg, phoneMsg: "Enter a valid phone number" },
      }));
      return false;
    },
    [digitRegex]
  );

  // Form handlers
  const handleInputChange = useCallback((name: string, value: any) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
  }, []);

  const handleCountryCodeSelect = useCallback(
    (data: { label: string; value: string }) => {
      const value = data ? data.value : "";
      setSelectedCountryCode(data);
      setFormData((prev) => ({ ...prev, countryCode: value }));

      if (data && formData.phoneNumber.length > 0) {
        validatePhone(formData.phoneNumber, value);
      }
    },
    [formData.phoneNumber, validatePhone]
  );

  const handleCountrySelect = useCallback(
    (data: { label: string; value: string }) => {
      const value = data ? data.value : "";
      setSelectedCountry(data);
      setFormData((prev) => ({ ...prev, country: value }));
    },
    []
  );

  const handlePartnerTypeClick = useCallback((value: string) => {
    setFormData((prev) => {
      const partnerTypeArray = [...prev.type];
      if (partnerTypeArray.includes(value)) {
        return {
          ...prev,
          type: partnerTypeArray.filter((type) => type !== value),
        };
      } else {
        return { ...prev, type: [...partnerTypeArray, value] };
      }
    });
  }, []);

  const handlePotentialChange = useCallback((key: string) => {
    setFormData((prev) => ({ ...prev, potential: key }));
  }, []);

  const handleDateChange = useCallback(
    (date: [Date | null, Date | null] | Date | null, name: string) => {
      setFormData((prev) => ({ ...prev, [name]: date }));
    },
    []
  );

  // Validation state
  const isFormValid = useMemo(() => {
    return (
      formData.name &&
      (!formData.accountManagerEmail ||
        emailRegex.test(formData.accountManagerEmail)) &&
      (!formData.email || emailRegex.test(formData.email))
    );
  }, [formData.name, formData.accountManagerEmail, formData.email, emailRegex]);

  return {
    formData,
    setFormData,
    validationState,
    selectedCountryCode,
    selectedCountry,
    setSelectedCountryCode,
    setSelectedCountry,
    countryCodeOptions,
    countryOptions,
    regionOptions,
    revenueStreamOptions,
    partnerVerticalOptions,
    partnerTierOptions,
    partnerTypes,
    revenueStream,
    partnerVertical,
    partnerTier,
    emailRegex,
    digitRegex,
    isLoading: isLoading || isAddLoading || isUpdateLoading,
    isFormValid,
    initializeLookupData,
    validateEmail,
    validateAccountManagerEmail,
    validatePhone,
    handleInputChange,
    handleCountryCodeSelect,
    handleCountrySelect,
    handlePartnerTypeClick,
    handlePotentialChange,
    handleDateChange,
  };
};
